import React, { useCallback, useEffect, useState } from "react";
import { AddFlex, CustomText } from "../../../reusableStyles";
import {
  BarChartRounded,
  FileDownload,
  Mail,
  PersonAddRounded,
  Phone,
  QuestionAnswerOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";
import {
  primaryActionColor,
  primaryActionColorLight,
} from "../../../constants";
import { callNonStoreApi } from "../../../NonStoreApis";
import {
  FetchInfluencerWiseStats,
  FetchInfluencerWiseStatsToOwner,
  GetCampaginWallStats,
  FetchCampaignNumbers,
} from "../../../Api/ApiEndPoints";
import { useParams } from "react-router";
import CampaginInfluencerData from "./CampaginInfluencerData";
import { BoxedTabs } from "../../../Pages/OfferingsPages/OfferingPagesTabHeader";
import styled from "styled-components";
import EmptyCaseUIComponent from "../../../Pages/Components/EmptyCaseUIComponent";
import { Parser } from "@json2csv/plainjs";
import { enqueueSnackbar } from "notistack";
import { format } from "date-fns";
import { Table } from "antd";

const DownloadButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background-color: ${primaryActionColor};
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${primaryActionColor}dd;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const stats = [
  {
    name: "Interaction Metrics",
    data: [
      {
        name: "Comments",
        icon: <QuestionAnswerOutlined style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        backgroundColor: primaryActionColorLight,
        key: "comments",
      },
      {
        name: "Shares",
        icon: <QuestionAnswerOutlined style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        backgroundColor: primaryActionColorLight,
        key: "share",
      },
      {
        name: "Story Interactions",
        icon: <QuestionAnswerOutlined style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        backgroundColor: primaryActionColorLight,
        key: "story",
      },
    ],
  },
  {
    name: "Engagement Metrics",
    data: [
      {
        name: "E-Mails",
        icon: <Mail style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        key: "email",
        backgroundColor: primaryActionColorLight,
      },
      {
        name: "Numbers",
        icon: <Phone style={{ fontSize: "18px" }} />,
        color: "#684EA6",
        key: "phoneNumber",
        backgroundColor: "#E2D9F7",
      },
      {
        name: "Followers",
        icon: <PersonAddRounded style={{ fontSize: "18px" }} />,
        color: "#6A1E55",
        key: "followers",
        backgroundColor: "#6A1E5533",
      },
      {
        name: "Fully Engaged Count",
        icon: <BarChartRounded style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        backgroundColor: primaryActionColorLight,
        key: "end",
      },
      {
        name: "Link Clicks",
        icon: <BarChartRounded style={{ fontSize: "18px" }} />,
        color: primaryActionColor,
        backgroundColor: primaryActionColorLight,
        key: "endLinkClicks",
      },
    ],
  },
];

function CampaignStats() {
  const [statsData, setStatsData] = useState({});
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [selectedTopTabIndex, setSelectedTopTabIndex] = useState(0);
  const [phoneNumbersData, setPhoneNumbersData] = useState([]);
  const { campaignId } = useParams();
  const handleStatsData = useCallback(async () => {
    try {
      const response = await callNonStoreApi(GetCampaginWallStats, {
        campaignId: campaignId,
      });
      setStatsData(response.data);
    } catch (error) {
      console.log(error);
    }
  }, [campaignId]);

  const handlePhoneNumbersData = useCallback(async () => {
    try {
      const response = await callNonStoreApi(FetchCampaignNumbers, {
        campaignId: campaignId,
      });

      // Transform API response to match the expected format
      const phoneNumbers = response.data.data.map((item, index) => ({
        id: item._id || index,
        username: item.username || "N/A",
        phone: item.phoneNumber || "N/A",
        name: item.name || "N/A",
        email: item.email || "N/A",
      }));

      setPhoneNumbersData(phoneNumbers);
    } catch (error) {
      console.log(error);
      enqueueSnackbar("Failed to fetch campaign numbers", {
        variant: "error",
      });
      setPhoneNumbersData([]);
    }
  }, [campaignId]);

  useEffect(() => {
    handleStatsData();
    handlePhoneNumbersData();
  }, [handleStatsData, handlePhoneNumbersData]);

  const handleDownloadPhoneNumbers = useCallback(async () => {
    try {
      if (phoneNumbersData.length === 0) {
        enqueueSnackbar("No phone numbers to export", {
          variant: "error",
        });
        return false;
      }

      // Format data for CSV export
      const csvData = phoneNumbersData.map((item) => ({
        Name: item.name,
        Username: item.username,
        "Phone Number": item.phone,
        Email: item.email,
      }));

      const parser = new Parser();
      const csv = parser.parse(csvData);

      // Create download link
      const encodedUri = encodeURIComponent(csv);
      const element = document.createElement("a");
      element.setAttribute("href", `data:text/csv;charset=utf-8,${encodedUri}`);
      element.setAttribute(
        "download",
        `Campaign_Phone_Numbers_${format(new Date(), "dd_MMM_yyyy")}.csv`
      );
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);

      enqueueSnackbar("Phone numbers exported successfully", {
        variant: "success",
      });
      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to export phone numbers", {
        variant: "error",
      });
      return false;
    }
  }, [phoneNumbersData]);

  const renderMetricsView = () => (
    <AddFlex wrap="wrap" gap="10px" flexDirection="column">
      {stats.map((item) => {
        return (
          <AddFlex flexDirection="column" gap="10px">
            <CustomText color="black" fontWeight="500">
              {item.name}
            </CustomText>
            <AddFlex
              gap="10px"
              wrap="wrap"
              style={{
                width: "max-content",
                border: "2px solid #ececec",
                borderRadius: "24px",
              }}
            >
              {item.data.map((stat, index) => (
                <AddFlex
                  padding="20px"
                  backgroundColor={"white"}
                  flexDirection="column"
                  gap="10px"
                  borderRadius="24px"
                  style={{ flexShrink: 0 }}
                >
                  <AddFlex gap="10px" alignItems="center">
                    {stat.icon && (
                      <AddFlex
                        width="30px"
                        height="30px"
                        alignItems="center"
                        justify="center"
                        backgroundColor={stat.backgroundColor}
                        style={{
                          borderRadius: "50%",
                          color: stat.color,
                          flexShrink: 0,
                        }}
                      >
                        {stat.icon}
                      </AddFlex>
                    )}
                    <CustomText
                      color="black"
                      fontSize="12px"
                      fontWeight={"500"}
                    >
                      {stat.name}
                    </CustomText>
                  </AddFlex>
                  <AddFlex gap="5px" alignItems="flex-start">
                    <CustomText
                      fontSize={"24px"}
                      color={stat.color}
                      fontWeight={"500"}
                    >
                      {statsData[stat.key] || "-"}
                    </CustomText>
                    {statsData[`unique_intertaction_${stat.key}`] > 0 && (
                      <AddFlex
                        alignItems="center"
                        gap="5px"
                        padding="2px 8px"
                        backgroundColor={"#DCFCE8"}
                        borderRadius="25px"
                      >
                        <TrendingUpOutlined
                          sx={{ fontSize: "12px", color: "#166534" }}
                        />
                        <CustomText
                          fontSize={"12px"}
                          color={"#166534"}
                          fontWeight={"500"}
                        >
                          {statsData[`unique_intertaction_${stat.key}`] || 0}{" "}
                          New
                        </CustomText>
                      </AddFlex>
                    )}
                  </AddFlex>
                </AddFlex>
              ))}
            </AddFlex>
          </AddFlex>
        );
      })}
      <div className="flex flex-col gap-5 mt-10">
        <BoxedTabs
          selectedTabIndex={selectedTabIndex}
          setSelectedTabIndex={setSelectedTabIndex}
          tabs={["All Influencers", "Shared To You"]}
        />
        <CampaginInfluencerData
          apiEndPoint={
            selectedTabIndex === 0
              ? FetchInfluencerWiseStats
              : FetchInfluencerWiseStatsToOwner
          }
        />
      </div>
    </AddFlex>
  );

  const renderNumbersView = () => {
    const columns = [
      {
        title: "Name",
        dataIndex: "name",
        key: "name",
        width: "25%",
        render: (text) => (
          <CustomText color="black" fontSize="14px" fontWeight="500">
            {text}
          </CustomText>
        ),
      },
      {
        title: "Username",
        dataIndex: "username",
        key: "username",
        width: "25%",
        render: (text) => (
          <CustomText color="black" fontSize="14px" fontWeight="500">
            @{text}
          </CustomText>
        ),
      },
      {
        title: "Phone Number",
        dataIndex: "phone",
        key: "phone",
        width: "25%",
        render: (text) => (
          <CustomText color="black" fontSize="14px" fontWeight="400">
            {text}
          </CustomText>
        ),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        width: "25%",
        render: (text) => (
          <CustomText color="black" fontSize="14px" fontWeight="400">
            {text}
          </CustomText>
        ),
      },
    ];

    return (
      <AddFlex flexDirection="column" gap="16px">
        {/* Header with Download Button */}
        <AddFlex justify="space-between" alignItems="center">
          <CustomText fontSize="18px" fontWeight="600" color="black">
            Phone Numbers ({phoneNumbersData.length})
          </CustomText>
          {phoneNumbersData.length > 0 && (
            <DownloadButton onClick={handleDownloadPhoneNumbers}>
              <FileDownload sx={{ fontSize: "16px" }} />
              Export CSV
            </DownloadButton>
          )}
        </AddFlex>

        {/* Ant Design Table */}
        <Table
          columns={columns}
          dataSource={phoneNumbersData}
          rowKey={(record) => record.id || record.username}
          pagination={false}
          sticky
          locale={{
            emptyText: (
              <EmptyCaseUIComponent
                emptyTitle="No Phone Numbers Found"
                emptyDescription="No phone numbers have been collected from this campaign yet."
              />
            ),
          }}
          style={{
            backgroundColor: "white",
            borderRadius: "4px",
            border: "1px solid #ececec",
          }}
        />
      </AddFlex>
    );
  };

  return (
    <AddFlex flexDirection="column" padding="20px 48px">
      <div className="flex flex-col gap-5 mb-8">
        <BoxedTabs
          selectedTabIndex={selectedTopTabIndex}
          setSelectedTabIndex={setSelectedTopTabIndex}
          tabs={["Metrics", "Numbers"]}
        />
      </div>

      {selectedTopTabIndex === 0 ? renderMetricsView() : renderNumbersView()}
    </AddFlex>
  );
}

export default CampaignStats;
